import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ComposeRoute, type PopoutHandoff } from './compose';
import { BuzzingBee } from '@/features/connection';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useMailStore } from '@/stores/mail';

// Standalone composer window. Opened by the "Pop out" button on /compose as a
// real browser window (window.open popup), so the main b-mail tab can be
// minimized or closed while the email keeps editing here.
//
// State handoff: the main window saves the draft first (server is the source
// of truth), then passes the live editor state keyed by a nonce — via
// localStorage when it fits (instant, race-free) and via BroadcastChannel
// either way (handles big attachments that exceed the localStorage quota).
// This shell waits for the payload, then renders the normal ComposeRoute
// seeded from it. "Dock" hands control back to the main window.
export function ComposePopoutRoute() {
  const [params] = useSearchParams();
  const nonce = params.get('h');
  const [handoff, setHandoff] = useState<PopoutHandoff | null>(null);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const loadUser = useAuthStore((s) => s.loadUser);
  const refreshMailboxes = useMailStore((s) => s.refreshMailboxes);

  // New window = fresh stores. Persisted slices (theme, user) rehydrate from
  // localStorage on their own; these calls pull the live server state.
  useEffect(() => { setTheme(theme); }, [theme, setTheme]);
  useEffect(() => { loadUser(); refreshMailboxes(); }, [loadUser, refreshMailboxes]);
  useEffect(() => { document.title = 'B-Mail — Composer'; }, []);

  useEffect(() => {
    if (!nonce) return; // cold-opened URL: behave as a plain new-message window
    // Fast path: payload already written before this window booted.
    try {
      const raw = localStorage.getItem(`bmail-popout-handoff:${nonce}`);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.nonce === nonce) { setHandoff(p); return; }
      }
    } catch { /* quota / parse — the BroadcastChannel path below covers it */ }
    if (!('BroadcastChannel' in window)) return;
    const bc = new BroadcastChannel('bmail-compose');
    const onMsg = (ev: MessageEvent) => {
      if (ev.data?.type === 'handoff' && ev.data?.nonce === nonce) setHandoff(ev.data.payload);
    };
    bc.addEventListener('message', onMsg);
    // If this window raced past the original broadcast, ask for a re-send.
    const t = setTimeout(() => {
      bc.postMessage({ type: 'handoff-request', nonce });
    }, 2000);
    return () => { clearTimeout(t); bc.removeEventListener('message', onMsg); bc.close(); };
  }, [nonce]);

  if (nonce && !handoff) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-3 bg-bmail-navy text-bmail-text">
        <BuzzingBee />
        <div className="text-sm text-bmail-text-secondary">Loading composer…</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-[var(--bg)] text-[var(--text-primary)]">
      <ComposeRoute popout handoff={handoff ?? undefined} />
    </div>
  );
}
